import { useState, useRef, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import useWebRTC from "@/hooks/useWebRTC";
import useWakeLock from "@/hooks/useWakeLock";
import { useSettings } from "@/hooks/useSettings";
import VoiceOrb from "@/components/VoiceOrb";
import SettingsModal from "@/components/SettingsModal";
import OneDriveSetupModal from "@/components/OneDriveSetupModal";
import UserMenu from "@/auth/UserMenu";

type ConversationStatus = "idle" | "connecting" | "listening" | "speaking" | "paused";

type CumulativeUsage = {
    inputText: number;
    inputAudio: number;
    inputCachedText: number;
    inputCachedAudio: number;
    outputText: number;
    outputAudio: number;
};

function TokenCounter({ usage }: { usage: CumulativeUsage }) {
    const fmt = (n: number) => n.toLocaleString();
    const netInputText  = usage.inputText  - usage.inputCachedText;
    const netInputAudio = usage.inputAudio - usage.inputCachedAudio;
    return (
        <div className="font-mono text-xs text-gray-500 leading-5">
            <div className="flex gap-2">
                <span>in</span>
                <span>txt <span className="text-gray-400">{fmt(netInputText)}</span></span>
                <span>aud <span className="text-gray-400">{fmt(netInputAudio)}</span></span>
            </div>
            <div className="flex gap-2">
                <span className="text-gray-600">cached</span>
                <span>txt <span className="text-gray-400">{fmt(usage.inputCachedText)}</span></span>
                <span>aud <span className="text-gray-400">{fmt(usage.inputCachedAudio)}</span></span>
            </div>
            <div className="flex gap-2">
                <span>out</span>
                <span>txt <span className="text-gray-400">{fmt(usage.outputText)}</span></span>
                <span>aud <span className="text-gray-400">{fmt(usage.outputAudio)}</span></span>
            </div>
        </div>
    );
}


export default function App() {
    const [isRecording, setIsRecording] = useState(false);
    const [status, setStatus] = useState<ConversationStatus>("idle");
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // OneDrive AI Notes state
    const [kbUrl, setKbUrl] = useState<string | null>(null);
    const [showOneDriveSetup, setShowOneDriveSetup] = useState(false);

    useWakeLock(isRecording);
    const { settings, isLoaded, saveSettings } = useSettings();
    
    const ZERO_USAGE: CumulativeUsage = { inputText: 0, inputAudio: 0, inputCachedText: 0, inputCachedAudio: 0, outputText: 0, outputAudio: 0 };
    const [usage, setUsage] = useState<CumulativeUsage>(ZERO_USAGE);

    const [availableDeployments, setAvailableDeployments] = useState<string[]>([]);
    const [availableVoices, setAvailableVoices] = useState<string[]>([]);
    const [selectedDeployment, setSelectedDeployment] = useState("");
    const [selectedVoice, setSelectedVoice] = useState("");

    const [configData, setConfigData] = useState<Record<string, unknown> | null>(null);

    // Fetch config once on mount
    useEffect(() => {
        fetch("/api/config")
            .then(r => r.json())
            .then(data => {
                setAvailableDeployments(data.deployments ?? []);
                setAvailableVoices(data.voices ?? []);
                setConfigData(data);

                if (data.onedrive_enabled) {
                    if (data.kb_web_url) {
                        setKbUrl(data.kb_web_url);
                    }
                    if (!localStorage.getItem("kb_intro_shown")) {
                        setShowOneDriveSetup(true);
                    }
                }
            })
            .catch(err => console.error("Failed to load config:", err));
    }, []);

    // Apply saved settings once both config and settings are loaded
    useEffect(() => {
        if (!isLoaded || !configData) return;
        const deployments = (configData.deployments as string[]) ?? [];
        const voices = (configData.voices as string[]) ?? [];
        if (settings) {
            setSelectedDeployment(settings.mode || deployments[0] || "");
            setSelectedVoice(settings.voice || voices[0] || "");
        } else {
            setSelectedDeployment(deployments[0] || "");
            setSelectedVoice(voices[0] || "");
        }
    }, [isLoaded, settings, configData]);

    const [orbIconVisible, setOrbIconVisible] = useState(false);
    const orbHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleOrbMouseActivity = () => {
        setOrbIconVisible(true);
        if (orbHideTimerRef.current) clearTimeout(orbHideTimerRef.current);
        orbHideTimerRef.current = setTimeout(() => setOrbIconVisible(false), 3000);
    };

    const handleOrbMouseLeave = () => {
        if (orbHideTimerRef.current) clearTimeout(orbHideTimerRef.current);
        setOrbIconVisible(false);
    };

    // Audio level ref — written by the recorder analyser, read by VoiceOrb canvas loop
    const levelRef = useRef<number>(0);

    const stopConversationRef = useRef<() => void>(() => {});

    const { startSession, stopSession, pauseSession, resumeSession } = useWebRTC({
        deployment: selectedDeployment || undefined,
        voice: selectedVoice || undefined,
        onSessionStarted: () => setStatus("listening"),
        onSessionEnded: () => { setStatus("idle"); setIsRecording(false); levelRef.current = 0; },
        onError: event => { 
            console.error("WebRTC error:", event);
            setErrorMessage("Connection failed. Please try again.");
            setIsRecording(false);
            setStatus("idle"); 
        },
        onReceivedResponseAudioTranscriptDelta: () => {
            console.log("📍 Audio transcript delta → speaking");
            setStatus("speaking");
        },
        onReceivedInputAudioBufferSpeechStarted: () => {
            console.log("📍 Speech started → listening");
            setStatus("listening");
        },
        onReceivedResponseDone: message => {
            if (message.response?.usage) {
                const u = message.response.usage;
                setUsage(prev => ({
                    inputText:        prev.inputText        + (u.input_token_details?.text_tokens  ?? 0),
                    inputAudio:       prev.inputAudio       + (u.input_token_details?.audio_tokens ?? 0),
                    inputCachedText:  prev.inputCachedText  + (u.input_token_details?.cached_tokens_details?.text_tokens  ?? 0),
                    inputCachedAudio: prev.inputCachedAudio + (u.input_token_details?.cached_tokens_details?.audio_tokens ?? 0),
                    outputText:       prev.outputText       + (u.output_token_details?.text_tokens  ?? 0),
                    outputAudio:      prev.outputAudio      + (u.output_token_details?.audio_tokens ?? 0),
                }));
            }
            setStatus("listening");
        },
        onReceivedExtensionToolResponse: message => {
            if (message.tool_name === "stop_conversation") {
                stopConversationRef.current();
            }
        },
        onReceivedOutputAudioBufferStopped: () => {
            console.log("📍 Output audio stopped → listening");
            setStatus("listening");
        },
        onAudioLevel: level => { 
            levelRef.current = level;
        },
    });

    const stopConversation = () => {
        stopSession();
        levelRef.current = 0;
        setIsRecording(false);
        setStatus("idle");
    };
    useEffect(() => {
        stopConversationRef.current = stopConversation;
        // stopConversation closes over `stopSession`, which is stable from useWebRTC.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stopSession]);

    const onToggleListening = async () => {
        if (status === "idle") {
            // New session: start and reset token counter
            setHasStarted(true);
            setErrorMessage(null);
            setIsRecording(true);
            setStatus("connecting");
            setUsage(ZERO_USAGE);
            try {
                await startSession();
            } catch (e) {
                console.error("Failed to start session:", e);
                setIsRecording(false);
                setStatus("idle");
                setErrorMessage("Failed to connect. Please try again.");
            }
        } else if (status === "paused") {
            resumeSession();
            setStatus("listening");
        } else if (status === "listening" || status === "speaking") {
            pauseSession();
            setStatus("paused");
        }
        // "connecting" — ignore clicks while handshake is in progress
    };

    const isListening = status === "listening";
    const isSpeaking  = status === "speaking";
    const isPaused    = status === "paused";

    return (
        <div className="flex min-h-screen flex-col bg-gray-950 text-gray-100">
            {isSettingsOpen && (
                <SettingsModal
                    isOpen={isSettingsOpen}
                    onClose={() => setIsSettingsOpen(false)}
                    settings={settings}
                    onSave={saveSettings}
                    deployments={availableDeployments}
                    voices={availableVoices}
                />
            )}
            <OneDriveSetupModal
                isOpen={showOneDriveSetup}
                onDismiss={() => {
                    localStorage.setItem("kb_intro_shown", "1");
                    setShowOneDriveSetup(false);
                }}
            />
            {/* Header */}
            <header className="border-b border-gray-800/60 px-6 py-4">
                <div className="mx-auto flex max-w-3xl items-center justify-between">
                    <span className="font-semibold tracking-tight">Talk</span>
                    <UserMenu onSettingsClick={() => setIsSettingsOpen(true)} kbUrl={kbUrl} />
                </div>
            </header>



            {/* Main */}
            <main className="flex flex-1 flex-col items-center justify-center overflow-hidden">
                <div className="flex items-center justify-center">
                    <button
                        onClick={onToggleListening}
                        onMouseEnter={handleOrbMouseActivity}
                        onMouseMove={handleOrbMouseActivity}
                        onMouseLeave={handleOrbMouseLeave}
                        aria-label={
                            status === "idle"    ? "Start conversation" :
                            status === "paused"  ? "Resume conversation" :
                            status === "connecting" ? "Connecting…" :
                            "Pause conversation"
                        }
                        disabled={status === "connecting"}
                        className="relative flex items-center justify-center focus:outline-none"
                        style={{ borderRadius: "50%" }}
                    >
                        <VoiceOrb
                            levelRef={levelRef}
                            isListening={isListening}
                            isSpeaking={isSpeaking}
                            size={390}
                        />
                        {/* Play / Pause / Resume icon overlay */}
                        <span
                            className="pointer-events-none absolute flex items-center justify-center"
                            style={{
                                opacity: orbIconVisible ? 1 : 0,
                                transition: orbIconVisible
                                    ? "opacity 100ms ease-in"
                                    : "opacity 400ms ease-out",
                            }}
                        >
                            {(isListening || isSpeaking)
                                ? <Pause className="h-16 w-16 text-white drop-shadow-lg" fill="white" />
                                : <Play  className="h-16 w-16 text-white drop-shadow-lg" fill="white" />
                            }
                        </span>
                    </button>
                </div>
                {/* Hint or error message */}
                <div className="mt-8 h-6 flex items-center justify-center">
                    {errorMessage ? (
                        <p className="text-sm text-red-400">{errorMessage}</p>
                    ) : status === "connecting" ? (
                        <p className="animate-connecting text-sm text-gray-500">Connecting…</p>
                    ) : isPaused ? (
                        <p className="text-sm text-gray-500">Paused — tap to resume</p>
                    ) : !hasStarted ? (
                        <p className="text-sm text-gray-500">Tap to talk</p>
                    ) : null}
                </div>
            </main>

            {/* Footer — centered token counter */}
            <footer className="shrink-0 border-t border-gray-800/60 px-4 py-5">
                <div className="mx-auto flex max-w-3xl flex-col items-center justify-center">
                    <TokenCounter usage={usage} />
                </div>
            </footer>
        </div>
    );
}

